import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import IncludePropertyInEvents from "./include-feature-flag-property-in-backend-events.mdx";
import NodeSetSendFeatureFlagsTrue from "./feature-flags-code-node-set-send-feature-flags-to-true.mdx";
import NodeOverrideServerProperties from './override-server-properties/node.mdx';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`There are 2 steps to implement feature flags in Node:`}</p>
    <h3 {...{
      "id": "step-1-evaluate-the-feature-flag-value"
    }}>{`Step 1: Evaluate the feature flag value`}</h3>
    <h4 {...{
      "id": "boolean-feature-flags"
    }}>{`Boolean feature flags`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-node"
      }}>{`const isFeatureFlagEnabled = await client.isFeatureEnabled('flag-key', 'distinct_id_of_your_user')
if (isFeatureFlagEnabled) {
    // Your code if the flag is enabled

    // Optional: fetch the payload
    const matchedFlagPayload = await client.getFeatureFlagPayload('flag-key', 'distinct_id_of_your_user', isFeatureFlagEnabled)
}
`}</code></pre>
    <h4 {...{
      "id": "multivariate-feature-flags"
    }}>{`Multivariate feature flags`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-node"
      }}>{`const enabledVariant = await client.getFeatureFlag('flag-key', 'distinct_id_of_your_user')

if (enabledVariant === 'variant-key') {  // replace 'variant-key' with the key of your variant
    // Do something differently for this user

    // Optional: fetch the payload
    const matchedFlagPayload = await client.getFeatureFlagPayload('flag-key', 'distinct_id_of_your_user', enabledVariant)
}
`}</code></pre>

    <IncludePropertyInEvents mdxType="IncludePropertyInEvents" />
    <p>{`There are two methods you can use to include feature flag information in your events:`}</p>
    <h4 {...{
      "id": "method-1-include-the-featurefeature_flag_name-property"
    }}>{`Method 1: Include the `}<inlineCode parentName="h4">{`$feature/feature_flag_name`}</inlineCode>{` property`}</h4>
    <p>{` In the event properties, include `}<inlineCode parentName="p">{`$feature/feature_flag_name: variant_key`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-node"
      }}>{`client.capture({
    distinctId: 'distinct_id_of_your_user',
    event: 'event_name',
    properties: {
        '$feature/feature-flag-key': 'variant-key' // replace feature-flag-key with your flag key. Replace 'variant-key' with the key of your variant
    },
})
`}</code></pre>
    <h4 {...{
      "id": "method-2-set-sendfeatureflags-to-true"
    }}>{`Method 2: Set `}<inlineCode parentName="h4">{`sendFeatureFlags`}</inlineCode>{` to `}<inlineCode parentName="h4">{`true`}</inlineCode></h4>

    <NodeSetSendFeatureFlagsTrue mdxType="NodeSetSendFeatureFlagsTrue" />
    <h3 {...{
      "id": "fetching-all-flags-for-a-user"
    }}>{`Fetching all flags for a user`}</h3>
    <p>{`You can fetch all flag values for a single user by calling `}<inlineCode parentName="p">{`getAllFlags()`}</inlineCode>{` or `}<inlineCode parentName="p">{`getAllFlagsAndPayloads()`}</inlineCode>{`.`}</p>
    <p>{`This is useful when you need to fetch multiple flag values and don't want to make multiple requests.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-node"
      }}>{`await client.getAllFlags('distinct_id_of_your_user') 
await client.getAllFlagsAndPayloads('distinct_id_of_your_user')
`}</code></pre>
    <h3 {...{
      "id": "sending-feature_flag_called-events"
    }}>{`Sending `}<inlineCode parentName="h3">{`$feature_flag_called`}</inlineCode>{` events`}</h3>
    <p>{`Capturing `}<inlineCode parentName="p">{`$feature_flag_called`}</inlineCode>{` events enable PostHog to know when a flag was accessed by a user and thus provide `}<a parentName="p" {...{
        "href": "/docs/product-analytics/insights"
      }}>{`analytics and insights`}</a>{` on the flag. By default, we send a these event when:`}</p>
    <ol>
      <li parentName="ol">{`You call `}<inlineCode parentName="li">{`posthog.getFeatureFlag()`}</inlineCode>{` or `}<inlineCode parentName="li">{`posthog.isFeatureEnabled()`}</inlineCode>{`, AND `}</li>
      <li parentName="ol">{`It's a new user, or the value of the flag has changed. `}</li>
    </ol>
    <blockquote>
      <p parentName="blockquote"><em parentName="p">{`Note:`}</em>{` Tracking whether it's a new user or if a flag value has changed happens in a local cache. This means that if you reinitialize the PostHog client, the cache resets as well – causing `}<inlineCode parentName="p">{`$feature_flag_called`}</inlineCode>{` events to be sent again when calling `}<inlineCode parentName="p">{`getFeatureFlag`}</inlineCode>{` or `}<inlineCode parentName="p">{`isFeatureEnabled`}</inlineCode>{`. PostHog is built to handle this, and so duplicate `}<inlineCode parentName="p">{`$feature_flag_called`}</inlineCode>{` events won't affect your analytics.`}</p>
    </blockquote>
    <p>{`You can disable automatically capturing `}<inlineCode parentName="p">{`$feature_flag_called`}</inlineCode>{` events. For example, when you don't need the analytics, or it's being called at such a high volume that sending events slows things down.`}</p>
    <p>{`To disable it, set the `}<inlineCode parentName="p">{`sendFeatureFlagEvents`}</inlineCode>{` argument in your function call, like so:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-node"
      }}>{`const isFeatureFlagEnabled = await client.isFeatureEnabled(
    'flag-key', 
    'distinct_id_of_your_user',
    {
        'sendFeatureFlagEvents': false
    })
`}</code></pre>

    <NodeOverrideServerProperties mdxType="NodeOverrideServerProperties" />
    <h3 {...{
      "id": "request-timeout"
    }}>{`Request timeout`}</h3>
    <p>{`You can configure the `}<inlineCode parentName="p">{`feature_flag_request_timeout_ms`}</inlineCode>{` parameter when initializing your PostHog client to set a flag request timeout. This helps prevent your code from being blocked in the case when PostHog's servers are too slow to respond. By default, this is set at 3 seconds.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const client = new PostHog('<ph_project_api_key>', { 
        api_host: '<ph_client_api_host>',
        feature_flag_request_timeout_ms: 3000 // Time in milliseconds. Default is 3000 (3 seconds).
    }
)
`}</code></pre>
    <h3 {...{
      "id": "error-handling"
    }}>{`Error handling`}</h3>
    <p>{`When using the PostHog SDK, it's important to handle potential errors that may occur during feature flag operations. Here's an example of how to wrap PostHog SDK methods in an error handler:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`async function handleFeatureFlag(client, flagKey, distinctId) {
    try {
        const isEnabled = await client.isFeatureEnabled(flagKey, distinctId);
        console.log(\`Feature flag '\${flagKey}' for user '\${distinctId}' is \${isEnabled ? 'enabled' : 'disabled'}\`);
        return isEnabled;
    } catch (error) {
        console.error(\`Error fetching feature flag '\${flagKey}': \${error.message}\`);
        // Optionally, you can return a default value or throw the error
        // return false; // Default to disabled
        throw error;
    }
}

// Usage example
try {
    const flagEnabled = await handleFeatureFlag(client, 'new-feature', 'user-123');
    if (flagEnabled) {
        // Implement new feature logic
    } else {
        // Implement old feature logic
    }
} catch (error) {
    // Handle the error at a higher level
    console.error('Feature flag check failed, using default behavior');
    // Implement fallback logic
}
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      